<template>
  <v-menu
    v-model="isMenuOpen"
    allow-overflow
    bottom
    max-width="600px"
    min-width="400px"
    offset-y
    :close-on-content-click="false"
  >
    <template #activator="{ on, attrs }">
      <v-btn
        icon
        class="ma-2"
        v-bind="attrs"
        v-on="on"
      >
        <v-icon
          v-if="!hasUnreadNotifications"
        >
          notifications_none
        </v-icon>

        <v-badge
          v-if="hasUnreadNotifications"
          overlap
          color="red"
          :content="unreadNotificationsCount"
        >
          <v-icon>
            notification_important
          </v-icon>
        </v-badge>
      </v-btn>
    </template>

    <v-card>
      <div class="text-center deep-purple accent-4 white--text pa-2">
        <v-icon
          dark
          class="mr-2"
        >
          notification_important
        </v-icon>
        <span class="text-subtitle-2 font-weight-bold text-center text--white">
          Unread Notifications
        </span>
      </div>

      <template v-if="hasUnreadNotifications">
        <div
          class="pa-0"
          style="max-height: 60vh; overflow-y: auto;"
        >
          <v-list
            v-for="(notification, i) of recentUnreadNotifications"
            :key="i"
            two-line
          >
            <v-list-item>
              <v-list-item-content>
                <v-list-item-subtitle
                  class="text--secondary text-caption"
                >
                  <v-tooltip top>
                    <template #activator="{ on, attrs }">
                      <span
                        v-bind="attrs"
                        v-on="on"
                      >{{ daysAgo(notification.created_at) }}</span>
                    </template>
                    <span>{{ dateTimeDisplayFormat(notification.created_at) }}</span>
                  </v-tooltip>
                </v-list-item-subtitle>
                <p class="text-body-2">
                  <!-- eslint-disable vue/no-v-html -->
                  <span v-html="notification.content" />
                  <!-- eslint-enable -->
                </p>
              </v-list-item-content>

              <v-list-item-action
                class="text--secondary flex-row align-center"
              >
                <v-tooltip top>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      class="ml-2"
                      icon
                      small
                      v-bind="attrs"
                      v-on="on"
                      @click="_ => goToNotification(notification)"
                    >
                      <v-icon>
                        mdi-message-arrow-right-outline
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Go to Notification</span>
                </v-tooltip>

                <v-tooltip top>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      class="ml-2"
                      icon
                      small
                      v-bind="attrs"
                      v-on="on"
                      @click="_ => setNotificationAsRead(notification.id)"
                    >
                      <v-icon>
                        mdi-check
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Mark as Read</span>
                </v-tooltip>
              </v-list-item-action>
            </v-list-item>
            <v-divider />
          </v-list>
        </div>
      </template>

      <template v-else>
        <p class="text-center pa-4 ma-0 text--secondary text-body-2">
          No unread notifications.
        </p>
      </template>

      <div class="text-center indigo lighten-5">
        <span
          key="go-to-notifications"
          style="cursor: pointer;"
          class="py-4 d-block text-body-2 font-weight-bold indigo--text text--lighten-1"
          @click="goToNotifications"
        >
          View All Activity
        </span>
      </div>
    </v-card>
  </v-menu>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { dateTimeDisplayFormat } from '@/utilities'
import moment from 'moment-timezone'

import { NOTIFICATION_NAMESPACE } from '@/store/notification'
import { alertHandler } from '@/alertHandler'
import { subscribeToNotificationChannel } from '@/channels/notifications_channel'

export default {
  name: 'NotificationsBox',

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      isMenuOpen: false,
    }
  },

  computed: {
    hasUnreadNotifications () {
      return this.unreadNotificationsCount > 0
    },

    ...mapGetters(NOTIFICATION_NAMESPACE, [
      'recentUnreadNotifications',
      'unreadNotificationsCount',
    ]),
  },

  async created () {
    await this.updateLatestUnreadNotifications()
    subscribeToNotificationChannel({ onUpdate: this.onNotificationReceived })
  },

  methods: {
    dateTimeDisplayFormat,

    daysAgo (notificationTime) {
      return moment(notificationTime).fromNow()
    },

    goToNotification (notification) {
      this.$router.push(`/notifications/${notification.id}`)
      this.isMenuOpen = false
    },

    goToNotifications () {
      if (this.$route.name !== 'notifications') this.$router.push('/notifications')
      this.isMenuOpen = false
    },

    onNotificationReceived (notification) {
      const content = notification.content.length > 150 ? `${notification.content.slice(0, 150)}...` : notification.content

      this.displayAlert(
        'info',
        `<strong>You received a new notification:</strong> ${content}`,
        null,
        {
          onClick: _ => {
            this.goToNotification(notification)
            this.setAlert(null)
          },
          timeout: null,
        }
      )

      this.addNotification(notification)
    },

    ...mapActions(NOTIFICATION_NAMESPACE, [
      'addNotification',
      'setNotificationAsRead',
      'updateLatestUnreadNotifications',
    ]),

    ...mapMutations([
      'setAlert',
    ]),
  },
}
</script>
